// @givmo/board — taking the whole minute book away.
//
// Plan §3.8: the organization is the sole owner and controller of its board records, and continuous
// self-service export in open formats is a build prerequisite rather than a feature. So this is
// available at all times to any board reader — no gate, no queue, no waiting period, and nothing
// about it is conditioned on the relationship continuing.
//
// TWO ARTIFACTS, and they are two views of ONE read: the JSON manifest, and a plain-text document the
// backend renders FROM that manifest so the two cannot disagree. Both are the whole book — the read
// underneath walks it in bounded batches by keyset, but a cap was never an option: an export that
// silently truncated a minute book would not be the complete copy §3.8 promises.
//
// ⚠ WHAT THE FINGERPRINT COVERS, STATED HONESTLY. `manifest_hash` binds the SET — each record's id,
// kind, state and content fingerprint — and deliberately not the generation time or the reader, so
// re-cutting an unchanged book reproduces the same value. It does NOT bind whole field values: a
// record's own content fingerprint covers its substantive content and excludes its storage pointers
// and its version-lineage columns (those are frozen by the database instead). And a reader who cannot
// see a closed session gets an export that reproduces on its own terms — it binds what THEY received
// rather than claiming to bind records it withheld. The panel says this in plain words; overstating
// it would be exactly the kind of assurance claim the module's vocabulary rail exists to prevent.

import type { OrgBoardOrgExportBoardMinuteBookResponse } from '@givmo/api-client';
import type { BoardOperations } from '../../board-operations';
import {
  CLOSED_RECORDS_HEADER,
  decodeClosedRecordsHeader,
  type CopyClosedRecords,
} from './copy-closed-records';

/**
 * CBG-RC12-EXPORT-WEB: a current director may take the copy again with the closed sessions' minutes
 * they weren't sent (`include_closed_sessions`, RC12-EXPORT's flag); each is then recorded as opened in
 * its history. The flag rides the same two reads, and for anyone else the backend ignores it.
 */
export interface CopyOptions {
  includeClosedSessions?: boolean;
}

/**
 * A filename a human and a filesystem will both accept, stamped with the day it was taken. A copy that
 * includes the closed sessions says so in its name, so a morning's two files aren't confused: the
 * suffix is approved on CBG-RC12-EXPORT-WEB's copy page (2026-10-14). The name follows what the
 * copy says it opened, never the flag sent: a reader who stopped being a current director before
 * taking it gets an ordinary copy, named as one.
 */
export function exportFileName(
  extension: 'txt' | 'json',
  nowMs: number = Date.now(),
  includesClosedSessions = false,
): string {
  const date = new Date(nowMs);
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const suffix = includesClosedSessions ? '-with-closed-sessions' : '';
  return `minute-book-${date.getFullYear()}-${month}-${day}${suffix}.${extension}`;
}

/** Hand a blob to the browser as a download, the shape `calendar-download.ts` established. */
function saveAs(contents: BlobPart, type: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([contents], { type }));
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Release on the next tick — revoking synchronously can race the click in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/**
 * Fetch the manifest and hand it over as JSON.
 *
 * Returns the manifest as well as saving it, so the caller can render the figures — the record count
 * and the fingerprint — from the very bytes that were downloaded rather than from a second read that
 * could differ.
 *
 * Throws on failure so the caller can surface it; a silent no-op would leave an officer clicking a
 * button that appears to do nothing.
 */
export async function downloadMinuteBookManifest(
  orgRef: string,
  operations: BoardOperations,
  options: CopyOptions = {},
): Promise<OrgBoardOrgExportBoardMinuteBookResponse> {
  const include = options.includeClosedSessions === true;
  const { data } = await operations.officerExportBoardMinuteBook({
    path: { org_ref: orgRef },
    ...(include ? { query: { include_closed_sessions: true } } : {}),
    throwOnError: true,
  });
  const opened = (data.opened_for_this_export ?? []).length > 0;
  saveAs(
    JSON.stringify(data, null, 2),
    'application/json;charset=utf-8',
    exportFileName('json', Date.now(), include && opened),
  );
  return data;
}

/**
 * Fetch the rendered document and hand it over as plain text.
 *
 * The response is `text/plain`, so the generated client parses it as text (`parseAs: 'auto'`) and the
 * envelope middleware leaves the non-JSON body untouched — the same path the `.ics` download takes.
 *
 * Returns what the copy says about the closed records it left out and opened, read from ITS OWN
 * response's header (CBG-RC12-EXPORT-WEB, the backend's §O9a), so the dialog describes the file just
 * saved; null when the copy reported nothing (`copy-closed-records.ts`).
 */
export async function downloadMinuteBookDocument(
  orgRef: string,
  operations: BoardOperations,
  options: CopyOptions = {},
): Promise<CopyClosedRecords | null> {
  const include = options.includeClosedSessions === true;
  const result = await operations.officerExportBoardMinuteBookDocument({
    path: { org_ref: orgRef },
    ...(include ? { query: { include_closed_sessions: true } } : {}),
    throwOnError: true,
  });
  const { data } = result;
  // A client without the response (a test's stub) reads as nothing reported.
  const response = (result as { response?: Response }).response;
  const closed = decodeClosedRecordsHeader(response?.headers.get(CLOSED_RECORDS_HEADER));
  saveAs(
    typeof data === 'string' ? data : String(data),
    'text/plain;charset=utf-8',
    exportFileName('txt', Date.now(), include && (closed?.opened.length ?? 0) > 0),
  );
  return closed;
}
